import { uniqBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { useSelector } from 'react-redux'

import SimpleDrawer from '@/components/drawers/simple-drawer'
import SignatureSigning from '@/modules/signature/components/signing/signature-signing'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconClose from '@assets/images/icons/v2/close.svg'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import IconMessages from '@assets/images/icons/notiz.svg'
import lang from '@/lang'
import TodoService from '@/modules/todo/todoService'
import TodoStatusEnum from '@/enums/TodoStatus.enum'
import { refreshList } from '@/redux/ducks/lists/requests/todoList'
import SignatureService from '@/modules/signature/signatureService'
import { Backdrop, CircularProgress, useTheme } from '@mui/material'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import TodoMessages from '@/modules/todo/components/todo-messages/todo-messages'
import ThemeInterface from '@/theme-interface'
import { Todo } from '@/modules/todo/types'
import { Signature, Signer, File } from '@/modules/signature/types'
import customDecodeUri from '@/utils/customDecodeUri'
import { useAppDispatch } from '@/redux/store'
import { errorCodes, experimentalFeatures } from '@/modules/base/util/constants'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  drawer: {
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    flexGrow: 1,
    flexDirection: 'column',
  },
  header: {
    height: 85,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.borderColor}`,
    flexShrink: 0,
    backgroundColor: theme.backgroundColor,
  },
  headerTitle: {
    fontSize: theme.fontSizes.large,
  },
  from: {
    fontSize: theme.fontSizes.small,
  },
  documentHeader: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0px 30px',
    flex: 1,
  },
  contentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    borderLeft: `1px solid ${theme.borderColors.default}`,
    width: 497,
    padding: 30,
  },
  close: {
    cursor: 'pointer',
  },
  loaderBackdrop: {
    background: 'rgba(255, 255, 255, 0.6)',
    zIndex: 10001,
  },
  loaderWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loader: {
    color: theme.primary,
  },
  customActionWrapper: {
    width: '100%',
  },
  commentButton: {
    position: 'relative',
    width: 45,
  },
  commentButtonBadge: {
    position: 'absolute',
    borderRadius: '50%',
    animationName: '$pulse',
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
    backgroundColor: theme.error,
    top: -3,
    right: -5,
    height: 13,
    width: 13,
  },
  customAction: {
    background: theme.white,
    border: `1px solid ${theme.borderColors.default}`,
  },
  commentsDrawer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  commentsRoot: {
    borderTop: `1px solid ${theme.borderColors.default}`,
    maxWidth: 497,
  },
  accessDeniedBanner: {
    backgroundColor: theme.errorBackground || '#fff3f3',
    borderRadius: 6,
    padding: '14px 16px',
    margin: '16px 30px 0',
  },
  accessDeniedBannerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  accessDeniedBannerHeadline: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  accessDeniedBannerTitle: {
    '& path': {
      fill: theme.errorText || '#8f2b2b',
    },
  },
  accessDeniedBannerTitleText: {
    color: theme.errorText || '#8f2b2b',
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.default,
    lineHeight: '22px',
  },
  accessDeniedBannerMessage: {
    color: theme.errorText || '#8f2b2b',
    fontSize: 14,
    lineHeight: '20px',
  },
  accessDeniedBannerClose: {
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
  },
  accessDeniedBannerCloseIcon: {
    '& path': {
      fill: theme.errorText || '#8f2b2b',
    },
  },
  // eslint-disable-next-line
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgb(231, 60, 91, 0.7)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 8px rgba(231, 60, 91, 0)',
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(231, 60, 91, 0)',
    },
  },
}))

interface TodoSigningDrawerProps {
  todo: Todo,
  onClose: () => void
}

type AccessDeniedError = {
  status?: number
  statusCode?: number
  body?: {
    status?: number
    statusCode?: number
    meta?: {
      resource?: Array<{ code?: string }>
    }
  }
}

const TodoSigningDrawer = (props: TodoSigningDrawerProps) => {
  const classes = useStyles()
  const theme: ThemeInterface = useTheme()
  const dispatch = useAppDispatch()
  const { todo, onClose } = props
  const { users, creator } = todo
  const activeUser = useSelector((state) => state.user.info)
  const [fetchedFile, setFetchedFile] = useState<File>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState<boolean>(false)
  const [unreadMessageCount, setUnreadMessageCount] = useState<number>(todo.unreadMessageCount)
  const [accessDeniedBannerDismissed, setAccessDeniedBannerDismissed] = useState<boolean>(false)
  const [showAccessDeniedBannerFromAction, setShowAccessDeniedBannerFromAction] = useState<boolean>(false)
  const isFolderPermissionsEnabled = activeUser?.experimentalFeatures?.includes(experimentalFeatures.folderPermissions)
  const hasInaccessibleDocuments = useMemo(() => {
    return Boolean(isFolderPermissionsEnabled && todo?.documents?.some((document) => document?.accessDenied === true))
  }, [isFolderPermissionsEnabled, todo?.documents])
  const primaryDocumentAccessDenied = useMemo(() => {
    return Boolean(isFolderPermissionsEnabled && todo?.documents?.[0]?.accessDenied === true)
  }, [isFolderPermissionsEnabled, todo?.documents])
  const shouldShowAccessDeniedBanner = (hasInaccessibleDocuments || showAccessDeniedBannerFromAction) && !accessDeniedBannerDismissed

  const signers: Signer[] = useMemo(() => {
    return uniqBy(todo.signatures.map((signature) => {
      const { name, signerEmail, signerId, signerType } = signature
      return {
        name,
        email: signerEmail,
        id: signerId,
        type: signerType,
      }
    }), 'email')
  }, [todo.signatures])
  const isUserSigner = useMemo(() => {
    return users.find((signer) => signer.email === activeUser.email)
  }, [todo.users])
  const isUserCreator = activeUser.id === creator.id

  const isAccessDeniedError = (error?: AccessDeniedError): boolean => {
    const statusCode = error?.status || error?.statusCode || error?.body?.status || error?.body?.statusCode
    const resourceCode = error?.body?.meta?.resource?.[0]?.code
    return statusCode === 403 || resourceCode === errorCodes.NOT_ALLOWED
  }

  useEffect(() => {
    if (primaryDocumentAccessDenied) {
      setFetchedFile(undefined)
      return
    }

    if (!todo?.documents?.[0]?.id) {
      setFetchedFile(undefined)
      return
    }

    setIsLoading(true)
    TodoService.fetchDocumentInfo(todo.documents[0].id)
      .then((data) => {
        setFetchedFile(data)
      })
      .catch((error) => {
        if (isFolderPermissionsEnabled && isAccessDeniedError(error)) {
          setAccessDeniedBannerDismissed(false)
          setShowAccessDeniedBannerFromAction(true)
          return
        }
        toast.showToast({
          type: 'error',
          message: lang.signature.signing.errors.fetchDocument,
        })
      })
      .finally(() => setIsLoading(false))
  }, [todo, isFolderPermissionsEnabled, primaryDocumentAccessDenied])

  useEffect(() => {
    setAccessDeniedBannerDismissed(false)
    setShowAccessDeniedBannerFromAction(false)
  }, [todo?.id])

  const isReadOnly = useMemo(() => {
    const userSignatures = todo.signatures?.filter(signature => signature.signerEmail === activeUser.email)
    const userSignaturesSigned = todo.signatures?.filter(signature => signature.signerEmail === activeUser.email && signature.signed)
    return [TodoStatusEnum.DONE, TodoStatusEnum.DECLINED].includes(todo.status)
      || todo.deletedAt !== null
      || todo.isReadOnly
      || (!todo.tenantCollaboration && userSignatures.length === userSignaturesSigned.length)
  }, [todo, activeUser])

  const handleSubmitClick = async(signatures: Signature[]): Promise<void> => {
    if (isReadOnly) { return }

    try {
      setIsLoading(true)
      await SignatureService.sign(todo.id, signatures)
      dispatch(refreshList())
      onClose()
    } catch {
      toast.showToast({ type: 'error', message: lang.signature.signing.errors.sign })
    } finally {
      setIsLoading(false)
    }
  }
  const handleRejectClick = async(): Promise<void> => {
    if (isReadOnly) { return }

    try {
      setIsLoading(true)
      await TodoService.decline(todo.id)
      dispatch(refreshList())
      onClose()
    } catch {
      toast.showToast({ type: 'error', message: lang.signature.signing.errors.reject })
    } finally {
      setIsLoading(false)
    }
  }
  const handleAbortClick = (): void => {
    onClose?.()
  }
  const handleCommentsClick = async(): Promise<void> => {
    try {
      await TodoService.markMessagesAsRead(todo.id)
      setUnreadMessageCount(0)
    } catch {
      // swallow
    } finally {
      setCommentsDrawerOpen(true)
    }
  }
  const handleCloseCommentsDrawer = (): void => {
    setCommentsDrawerOpen(false)
  }
  const handleSendInfoClick = async(email: string): Promise<void> => {
    try {
      await TodoService.sendSignatureInfoMail(todo.id, [email])
      toast.showToast({ type: 'success', message: lang.signature.signing.success.sendEmail })
    } catch {
      toast.showToast({ type: 'error', message: lang.signature.signing.errors.sendEmail })
    }
  }

  const commentsAction = (isUserSigner || isUserCreator) && (
    <div className={classes.customActionWrapper}>
      <div className={classes.commentButton}>
        <ButtonIconSquare
          className={classes.customAction}
          icon={IconMessages}
          iconFill={theme.paleBlue}
          color='custom'
          onClick={handleCommentsClick}
          debounceTime={0}
          variant='outline' />
        {unreadMessageCount > 0 && <span className={classes.commentButtonBadge}></span>}
      </div>
    </div>
  )

  const loader = useMemo(() => {
    if (!isLoading) { return null }
    return (
      <Backdrop open={true} classes={{ root: classes.loaderBackdrop }}>
        <div className={classes.loaderWrapper}>
          <CircularProgress color="primary" classes={{ colorPrimary: classes.loader }} />
        </div>
      </Backdrop>
    )
  }, [isLoading])

  const headerTitle = fetchedFile
    ? `${lang.todo.signing.title.document} ${customDecodeUri(fetchedFile?.name)}`
    : lang.todo.signing.title.default

  const content = (
    <>
      <div className={classes.header}>
        <div className={classes.documentHeader}>
          <span className={classes.headerTitle}>{headerTitle}</span>
          <span className={classes.from}>{lang.todo.signing.from} {todo.creator.firstName} {todo.creator.lastName}</span>
        </div>
        <div className={classes.contentHeader}>
          <span className={classes.headerTitle}>{lang.todo.detail.title}</span>
          <SVGIcon onClick={onClose} className={classes.close} svg={IconClose} width="12px" height="12px" />
        </div>
      </div>
      {shouldShowAccessDeniedBanner && (
        <div className={classes.accessDeniedBanner}>
          <div className={classes.accessDeniedBannerHeader}>
            <div className={classes.accessDeniedBannerHeadline}>
              <ErrorOutlineIcon className={classes.accessDeniedBannerTitle} style={{ width: 20, height: 20 }} />
              <span className={classes.accessDeniedBannerTitleText}>{lang.todo.detail.accessDeniedTitle}</span>
            </div>
            <button
              className={classes.accessDeniedBannerClose}
              onClick={() => setAccessDeniedBannerDismissed(true)}
              type='button'
              aria-label={lang.closeButtonLabel}
            >
              <SVGIcon svg={IconClose} width='12px' height='12px' customClass={classes.accessDeniedBannerCloseIcon} />
            </button>
          </div>
          <div className={classes.accessDeniedBannerMessage}>{lang.todo.detail.accessDeniedMessage}</div>
        </div>
      )}
      <SignatureSigning
        readOnly={isReadOnly || primaryDocumentAccessDenied}
        message={todo.displayContent}
        file={primaryDocumentAccessDenied ? undefined : fetchedFile}
        signers={signers}
        signatureItems={todo.signatures}
        creator={todo.creator}
        dueDate={todo.dueToDate}
        status={todo.status}
        clerks={todo.tenantCollaboration?.clerks || []}
        customActions={commentsAction}
        onSubmit={handleSubmitClick}
        onReject={handleRejectClick}
        onAbort={handleAbortClick}
        onSend={handleSendInfoClick} />
    </>
  )

  return (
    <>
      <SimpleDrawer open noHeader className={classes.drawer} onClose={() => onClose?.()}>
        {loader}
        {content}
      </SimpleDrawer>
      <SimpleDrawer
        className={classes.commentsDrawer}
        headline={lang.todo.messages.title}
        open={commentsDrawerOpen}
        onClose={handleCloseCommentsDrawer}>
        <TodoMessages
          showTitle={false}
          todo={todo}
          limit={6}
          className={classes.commentsRoot} />
      </SimpleDrawer>
    </>
  )
}

export default TodoSigningDrawer
